import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#randomStart();
    this.play();
  }

  /**
   * Le Shop 17 : en mode aleatoire, le message affiche au chargement est lui
   * aussi tire au sort. Le fondu est neutralise le temps de ce premier choix
   * pour eviter que le visiteur voie le premier message puis un autre.
   */
  #randomStart() {
    if (!this.hasAttribute('random')) return;

    const count = (this.refs.slides ?? []).length;
    if (count < 2) return;

    const start = Math.floor(Math.random() * count);
    if (start === 0) return;

    this.setAttribute('no-transition', '');
    this.current = start;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.removeAttribute('no-transition'));
    });
  }

  /**
   * Le Shop 17 : lorsque l'attribut `random` est present, le message suivant
   * est tire au hasard parmi les autres, sans jamais repeter celui affiche.
   * Sans cet attribut, le comportement sequentiel d'origine est conserve.
   */
  next() {
    const slides = this.refs.slides ?? [];
    const count = slides.length;

    if (this.hasAttribute('random') && count > 2) {
      const currentIndex = ((this.#current % count) + count) % count;
      let nextIndex = Math.floor(Math.random() * (count - 1));

      if (nextIndex >= currentIndex) nextIndex += 1;

      this.current = nextIndex;
      return;
    }

    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseFloat(`${interval}`);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    /**
     * Le Shop 17 : animation slide. Le message entrant glisse depuis la
     * droite, le message sortant glisse vers la gauche. Les classes ne sont
     * posees que lors d'un vrai changement (jamais au chargement), donc
     * aucun flash initial. Ignorees pendant le tirage au sort du premier
     * message (attribut no-transition).
     */
    const animate = !this.hasAttribute('no-transition');

    this.refs.slides?.forEach((slide, index) => {
      const hide = index !== relativeIndex;
      const wasHidden = slide.getAttribute('aria-hidden') === 'true';

      slide.setAttribute('aria-hidden', `${hide}`);

      if (!animate) {
        slide.classList.remove('ls17-slide-in', 'ls17-slide-out');
        return;
      }

      if (hide && !wasHidden) {
        slide.classList.remove('ls17-slide-in');
        slide.classList.add('ls17-slide-out');
      } else if (!hide && wasHidden) {
        slide.classList.remove('ls17-slide-out');
        slide.classList.add('ls17-slide-in');
      }
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
